import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, FormControlLabel, Grid, InputAdornment, Link, MenuItem, Paper, Stack, Switch, TextField as MuiTextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { appliesCountyLoanLimit, evaluateCountyLoanLimits, fetchCountyLimitsForPricing } from '../../services/pricingEngineService';
import pricingApi, { runPricingWithEngine } from '../../services/pricingApi';
import configurationApi from '../../services/configurationApi';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import brokerCompensationApi from '../../services/brokerCompensationApi';
import { useGetCountiesByStateQuery, useLazyGetCountyLoanLimitQuery } from '../../services/countyLoanLimitApi';
import StatusBadge from '../../components/shared/StatusBadge';
import CurrencyInput from '../../components/shared/CurrencyInput';
import PageHeader from '../../components/shared/PageHeader';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { ProductCard, SelectedRateDetail, fmtDollar } from '../../components/shared/pricing';
import { Roles } from '../../constants';
import { authService } from '../../services/authService';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import FormProvider from '../../components/form/FormProvider';
import SelectField from '../../components/form/SelectField';
import NumberField from '../../components/form/NumberField';
import CurrencyField from '../../components/form/CurrencyField';
import SwitchField from '../../components/form/SwitchField';

// ── Helpers ──
const toProperCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// ── Constants ──
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const LOCK_DURATIONS = [15, 30, 45];
const LOAN_TERMS = [30, 25, 20, 15, 10];
/** Shown when Non-QM is selected (40yr IO / DSCR, etc.) */
const LOAN_TERMS_NON_QM = [40, ...LOAN_TERMS];

/** Non-QM pricing should always search 40yr alongside selected terms (deduped, descending). */
function termsForPricingRequest(terms, productFamilies) {
  const t = Array.isArray(terms) ? terms : [];
  if (!productFamilies?.includes('Non-QM')) return t;
  const merged = t.includes(40) ? [...t] : [...t, 40];
  return [...new Set(merged)].sort((a, b) => b - a);
}
const PRODUCT_FAMILIES = [
  { id: 'Conventional', label: 'Conventional' },
  { id: 'FHA', label: 'FHA' },
  { id: 'VA', label: 'VA' },
  { id: 'USDA', label: 'USDA' },
  { id: 'Non-QM', label: 'Non-QM' },
];

const OCCUPANCY_OPTIONS = [
  { value: 'OwnerOccupied', label: 'Primary Residence' },
  { value: 'SecondHome', label: 'Second Home' },
  { value: 'NonOwnerOccupied', label: 'Investment Property' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: 'SingleFamilyResidence', label: 'Single Family' },
  { value: 'Condo', label: 'Condominium' },
  { value: 'Cooperative', label: 'Cooperative' },
  { value: 'MultiUnit', label: 'Multi-Unit (2-4)' },
  { value: 'ManufacturedHome', label: 'Manufactured Home' },
  { value: 'PlannedUnitDevelopment', label: 'PUD' },
];

const LOAN_PURPOSE_OPTIONS = [
  { value: 'Purchase', label: 'Purchase' },
  { value: 'RateTermRefinance', label: 'Rate/Term Refinance' },
  { value: 'CashoutRefinance', label: 'Cash-Out Refinance' },
];

const CITIZENSHIP_OPTIONS = [
  { value: 'USCitizen', label: 'US Citizen' },
  { value: 'PermanentResidentAlien', label: 'Permanent Resident' },
  { value: 'NonPermanentResidentAlien', label: 'Non-Permanent Resident' },
  { value: 'ForeignNational', label: 'Foreign National' },
];

const DOC_TYPE_OPTIONS = [
  { value: 'FullDoc', label: 'Full Documentation' },
  { value: 'BankStatements', label: 'Bank Statement' },
  { value: 'AssetDepletion', label: 'Asset Depletion' },
  { value: 'DSCR', label: 'DSCR' },
  { value: 'Streamline', label: 'Streamline Refinance' },
];

const ESCROW_OPTIONS = [
  { value: 'TaxesAndInsurance', label: 'Taxes & Insurance' },
  { value: 'TaxesOnly', label: 'Taxes Only' },
  { value: 'InsuranceOnly', label: 'Insurance Only' },
  { value: 'Waived', label: 'No Escrow (Waived)' },
];

const BANK_STATEMENT_TYPE_OPTIONS = [
  { value: 'PersonalTwelveMonths', label: 'Personal (12 Months)' },
  { value: 'BusinessTwelveMonths', label: 'Business (12 Months)' },
  { value: 'BlendedTwelveMonths', label: 'Blended (12 Months)' },
];

const PREPAY_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'OneYearHardPrepay', label: '1-yr Hard PP' },
  { value: 'TwoYearHardPrepay', label: '2-yr Hard PP' },
  { value: 'ThreeYearHardPrepay', label: '3-yr Hard PP' },
  { value: 'FiveYearHardPrepay', label: '5-yr Hard PP' },
];

const STATE_OPTIONS = US_STATES.map(s => ({ value: s, label: s }));
const LOCK_DURATION_OPTIONS = LOCK_DURATIONS.map(d => ({ value: d, label: `${d} Days` }));
const UNIT_OPTIONS = [{ value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }];
const COMP_TYPE_OPTIONS = [
  { value: 'LPC', label: 'LPC (Lender Paid)' },
  { value: 'BPC', label: 'BPC (Borrower Paid)' },
];
const BPC_METHOD_OPTIONS = [
  { value: 'PercentOfLoan', label: 'Percent' },
  { value: 'FixedAmount', label: 'Fixed Amount' },
];
const CHANNEL_OPTIONS = [
  { value: 'Retail', label: 'Retail' },
  { value: 'Wholesale', label: 'Wholesale' },
];

/** Sentinel broker row for lender staff / AE — run wholesale pricing without a real broker (no LPC from comp). */
const BASE_PRICING_BROKER_ID = '__base_pricing__';
const BASE_PRICING_BROKER = {
  id: BASE_PRICING_BROKER_ID,
  name: 'Base Pricing',
  nmls: null,
  compId: null,
};

/** One-click presets: merge over QUICK_PRICER_BASE_DEFAULTS; broker/channel preserved from current form */
const QUICK_SCENARIOS = [
  {
    id: 'conv-80-purch',
    label: 'Conv · 80% · Purchase',
    families: ['Conventional'],
    terms: [30],
    formPatch: {},
  },
  {
    id: 'dscr-125',
    label: 'DSCR 1.25 · Purchase',
    families: ['Non-QM'],
    terms: [30],
    formPatch: {
      loanPurpose: 'Purchase',
      loanAmount: 400000,
      purchasePrice: 500000,
      appraisedValue: 500000,
      docType: 'DSCR',
      dscrRatio: 1.25,
      prepayPenaltyTerm: 'ThreeYearHardPrepay',
      selfEmployed: true,
      occupancy: 'NonOwnerOccupied',
      veteran: false,
      subordinateFinancing: false,
      subordinateAmount: '',
    },
  },
  {
    id: 'fha-965-620',
    label: 'FHA · 96.5% LTV · 620 FICO',
    families: ['FHA'],
    terms: [30],
    formPatch: {
      loanPurpose: 'Purchase',
      loanAmount: 482500,
      purchasePrice: 500000,
      appraisedValue: 500000,
      docType: 'FullDoc',
      prepayPenaltyTerm: 'None',
      creditScore: 620,
      occupancy: 'OwnerOccupied',
      veteran: false,
      selfEmployed: false,
      subordinateFinancing: false,
    },
  },
  {
    id: 'va-irrrl-100',
    label: 'VA IRRRL · 100% LTV',
    families: ['VA'],
    terms: [30],
    formPatch: {
      loanPurpose: 'RateTermRefinance',
      docType: 'Streamline',
      loanAmount: 400000,
      cashoutAmount: '',
      appraisedValue: 400000,
      purchasePrice: 500000,
      veteran: true,
      occupancy: 'OwnerOccupied',
      selfEmployed: false,
      subordinateFinancing: false,
    },
  },
  {
    id: 'conv-co-80',
    label: 'Conv · Cash-out · 80% LTV',
    families: ['Conventional'],
    terms: [30],
    formPatch: {
      loanPurpose: 'CashoutRefinance',
      docType: 'FullDoc',
      loanAmount: 400000,
      cashoutAmount: 80000,
      appraisedValue: 500000,
      purchasePrice: 500000,
      veteran: false,
      occupancy: 'OwnerOccupied',
      selfEmployed: false,
      subordinateFinancing: false,
    },
  },
];

/** Baseline Quick Pricer defaults — scenario 1 matches these values */
const QUICK_PRICER_BASE_DEFAULTS = {
  loanPurpose: 'Purchase',
  loanAmount: 400000,
  cashoutAmount: '',
  docType: 'FullDoc',
  escrowType: 'TaxesAndInsurance',
  dscrRatio: '',
  prepayPenaltyTerm: 'None',
  bankStatementType: 'PersonalTwelveMonths',
  subordinateFinancing: false,
  subordinateAmount: '',
  propertyState: 'CA',
  selectedCounty: '',
  propertyType: 'SingleFamilyResidence',
  numberOfUnits: 1,
  purchasePrice: 500000,
  appraisedValue: 500000,
  creditScore: 740,
  citizenship: 'USCitizen',
  dti: 35,
  occupancy: 'OwnerOccupied',
  monthsReserves: 12,
  firstTimeBuyer: false,
  selfEmployed: false,
  veteran: false,
  lockDuration: 30,
  channel: 'Wholesale',
  brokerCompanyId: '',
  compensationType: 'LPC',
  bpcCalcMethod: 'PercentOfLoan',
  bpcValue: '',
};

const pricingSchema = z.object({
  // Loan
  loanPurpose: z.string(),
  loanAmount: z.number().or(z.literal('')),
  cashoutAmount: z.number().or(z.literal('')),
  docType: z.string(),
  escrowType: z.string(),
  dscrRatio: z.number().or(z.literal('')),
  bankStatementType: z.string(),
  prepayPenaltyTerm: z.string(),
  subordinateFinancing: z.boolean(),
  subordinateAmount: z.number().or(z.literal('')),
  // Property
  propertyState: z.string(),
  selectedCounty: z.string(),
  propertyType: z.string(),
  numberOfUnits: z.number(),
  purchasePrice: z.number().or(z.literal('')),
  appraisedValue: z.number().or(z.literal('')),
  // Borrower
  creditScore: z.number().or(z.literal('')),
  citizenship: z.string(),
  dti: z.number().or(z.literal('')),
  occupancy: z.string(),
  monthsReserves: z.number().or(z.literal('')),
  firstTimeBuyer: z.boolean(),
  selfEmployed: z.boolean(),
  veteran: z.boolean(),
  // Filters
  lockDuration: z.number(),
  channel: z.string(),
  brokerCompanyId: z.string(),
  // Compensation
  compensationType: z.string(),
  bpcCalcMethod: z.string(),
  bpcValue: z.string(),
});

const ScenarioPricerPage = () => {
  const currentUser = authService.getCurrentUser();
  const role = currentUser?.role;
  const isBrokerRole = [Roles.BrokerAdmin, Roles.Broker, Roles.Processor, Roles.BrokerFullAccessReadOnly].includes(role);
  const isAeRole = [Roles.AccountExecutive].includes(role);
  const isLenderRole = [Roles.Admin, Roles.LenderAdmin].includes(role);

  const { data: myBrokerCompany } = brokerCompanyApi.endpoints.getMyCompany.useQuery(undefined, { skip: !isBrokerRole });
  const { data: allBrokerCompanies = [] } = brokerCompanyApi.endpoints.getBrokerCompanies.useQuery(undefined, { skip: !isLenderRole });
  const { data: myAssignedBrokerCompanies = [] } = brokerCompanyApi.endpoints.getMyAssignedBrokerCompanies.useQuery(undefined, { skip: !isAeRole });

  // ── Form ──
  const methods = useForm({
    resolver: zodResolver(pricingSchema),
    defaultValues: { ...QUICK_PRICER_BASE_DEFAULTS },
  });
  const { watch, setValue, getValues, reset, control } = methods;
  const { touchedFields } = useFormState({ control, name: ['appraisedValue'] });

  // ── Watched form values ──
  const loanPurpose = watch('loanPurpose');
  const loanAmount = watch('loanAmount');
  const docType = watch('docType');
  const subordinateFinancing = watch('subordinateFinancing');
  const subordinateAmount = watch('subordinateAmount');
  const propertyState = watch('propertyState');
  const selectedCounty = watch('selectedCounty');
  const propertyType = watch('propertyType');
  const numberOfUnits = watch('numberOfUnits');
  const purchasePrice = watch('purchasePrice');
  const appraisedValue = watch('appraisedValue');
  const creditScore = watch('creditScore');
  const occupancy = watch('occupancy');
  const veteran = watch('veteran');
  const compensationType = watch('compensationType');
  const bpcCalcMethod = watch('bpcCalcMethod');
  const bpcValue = watch('bpcValue');
  const lockDuration = watch('lockDuration');
  const channel = watch('channel');
  const brokerCompanyId = watch('brokerCompanyId');

  const showChannelSelector = isLenderRole;
  const effectiveChannel = showChannelSelector ? channel : 'Wholesale';
  const isWholesale = effectiveChannel === 'Wholesale';
  const showStaffBrokerPicker = (isLenderRole && isWholesale) || isAeRole;

  const isRefi = loanPurpose === 'RateTermRefinance' || loanPurpose === 'CashoutRefinance';
  const isCashout = loanPurpose === 'CashoutRefinance';

  // ── Auto-mirror purchase price → appraised value (purchase loans only) ──
  // Mirrors while the user types purchase price. Stops only when they touch
  // appraised value and enter something different (touchedFields + value check).
  const isAppraisedOverriddenByUser = useCallback(() => {
    if (!touchedFields.appraisedValue) return false;
    const raw = methods.getValues('appraisedValue');
    const appraised = Number(raw);
    const purchase = Number(methods.getValues('purchasePrice'));
    if (raw === '' || raw == null || !Number.isFinite(appraised) || appraised <= 0) return false;
    return appraised !== purchase;
  }, [methods, touchedFields.appraisedValue]);

  const syncAppraisedFromPurchase = useCallback(() => {
    const purpose = methods.getValues('loanPurpose');
    const purchaseMode = purpose !== 'RateTermRefinance' && purpose !== 'CashoutRefinance';
    if (!purchaseMode) return;
    const price = Number(methods.getValues('purchasePrice'));
    const current = Number(methods.getValues('appraisedValue'));
    if (!Number.isFinite(price) || price <= 0) return;
    if (current === price) return;
    methods.setValue('appraisedValue', price, { shouldDirty: true, shouldTouch: false });
  }, [methods]);

  useEffect(() => {
    const subscription = methods.watch((_values, { name }) => {
      if (name === 'purchasePrice') {
        if (isAppraisedOverriddenByUser()) return;
        syncAppraisedFromPurchase();
        return;
      }

      if (name === 'loanPurpose') {
        syncAppraisedFromPurchase();
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, syncAppraisedFromPurchase, isAppraisedOverriddenByUser]);

  const effectiveLoanAmount = Number(loanAmount) || 0;

  // ── County lookup ──
  const { data: rawCounties = [], isFetching: isFetchingCounties } = useGetCountiesByStateQuery(
    { stateCode: propertyState, year: 2026 },
    { skip: !propertyState }
  );
  const counties = useMemo(() => {
    const seen = new Set();
    return rawCounties.filter(c => { if (seen.has(c.fipsCode)) return false; seen.add(c.fipsCode); return true; });
  }, [rawCounties]);
  const [fetchCountyLimit] = useLazyGetCountyLoanLimitQuery();

  // Auto-select first county when counties load
  useEffect(() => {
    if (counties.length > 0 && !selectedCounty) {
      setValue('selectedCounty', counties[0].fipsCode);
    }
  }, [counties, selectedCounty, setValue]);

  const availableBrokerCompanies = useMemo(() => {
    if (isBrokerRole) {
      return myBrokerCompany ? [myBrokerCompany] : [];
    }

    let companies = [];
    if (isAeRole) companies = [...myAssignedBrokerCompanies];
    else if (isLenderRole) companies = [...allBrokerCompanies];

    const sorted = [...companies].sort((a, b) => {
      const byName = (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      return (a?.nmls || '').localeCompare(b?.nmls || '', undefined, { sensitivity: 'base' });
    });

    if (isAeRole || isLenderRole) {
      return [BASE_PRICING_BROKER, ...sorted];
    }
    return sorted;
  }, [allBrokerCompanies, isAeRole, isBrokerRole, isLenderRole, myAssignedBrokerCompanies, myBrokerCompany]);

  const brokerCompanyOptions = useMemo(() => {
    return availableBrokerCompanies.map((company) => ({
      value: company.id,
      label: company.nmls ? `${company.name} (${company.nmls})` : company.name,
    }));
  }, [availableBrokerCompanies]);

  useEffect(() => {
    if (!isWholesale) {
      if (brokerCompanyId) setValue('brokerCompanyId', '');
      return;
    }

    if (brokerCompanyId && availableBrokerCompanies.some((company) => company.id === brokerCompanyId)) {
      return;
    }

    const staffListHasBase = (isAeRole || isLenderRole) && availableBrokerCompanies.some((c) => c.id === BASE_PRICING_BROKER_ID);
    if (staffListHasBase) {
      setValue('brokerCompanyId', BASE_PRICING_BROKER_ID);
      return;
    }

    if (availableBrokerCompanies.length === 1) {
      setValue('brokerCompanyId', availableBrokerCompanies[0].id);
      return;
    }

    if (brokerCompanyId) {
      setValue('brokerCompanyId', '');
    }
  }, [availableBrokerCompanies, brokerCompanyId, isAeRole, isLenderRole, isWholesale, setValue]);

  const isBasePricingSelection = isWholesale && brokerCompanyId === BASE_PRICING_BROKER_ID;

  const selectedWholesaleBrokerCompany = useMemo(() => {
    if (!isWholesale || !brokerCompanyId || brokerCompanyId === BASE_PRICING_BROKER_ID) return null;
    return availableBrokerCompanies.find((company) => company.id === brokerCompanyId) || null;
  }, [availableBrokerCompanies, brokerCompanyId, isWholesale]);

  /** Broker workspace → /broker/compensation; lender/AE → broker detail (comp panel) for selected company */
  const compensationManagementHref = useMemo(() => {
    if (isBrokerRole) return '/broker/compensation';
    if ((isLenderRole || role === Roles.AccountExecutive) && selectedWholesaleBrokerCompany?.id) {
      return `/lender/brokers/${selectedWholesaleBrokerCompany.id}`;
    }
    return null;
  }, [isBrokerRole, isLenderRole, role, selectedWholesaleBrokerCompany?.id]);
  const isSelectedBrokerApproved = selectedWholesaleBrokerCompany?.status === 'Active';

  // ── Active compensation (for LPC display) ──
  const { data: activeCompensation, isLoading: isLoadingComp } = brokerCompensationApi.endpoints.getActiveCompensation.useQuery(
    selectedWholesaleBrokerCompany?.id,
    { skip: !selectedWholesaleBrokerCompany?.id }
  );

  // ── Compute LPC comp amount ──
  const lpcCompDollars = useMemo(() => {
    if (!activeCompensation || compensationType !== 'LPC') return null;
    let dollars = (activeCompensation.percentage || 0) / 100 * (Number(loanAmount) || 0);
    if (activeCompensation.minDollarAmount != null && dollars < activeCompensation.minDollarAmount)
      dollars = activeCompensation.minDollarAmount;
    if (activeCompensation.maxDollarAmount != null && dollars > activeCompensation.maxDollarAmount)
      dollars = activeCompensation.maxDollarAmount;
    return dollars;
  }, [activeCompensation, compensationType, loanAmount]);

  // ── Filter inputs (ToggleButtonGroups — not in useForm) ──
  const [selectedFamilies, setSelectedFamilies] = useState(['Conventional']);
  const [selectedTerms, setSelectedTerms] = useState([30]);

  const loanTermOptions = useMemo(
    () => (selectedFamilies.includes('Non-QM') ? LOAN_TERMS_NON_QM : LOAN_TERMS),
    [selectedFamilies]
  );

  useEffect(() => {
    if (!selectedFamilies.includes('Non-QM')) {
      setSelectedTerms((prev) => {
        const next = prev.filter((t) => t !== 40);
        return next.length > 0 ? next : [30];
      });
    }
  }, [selectedFamilies]);

  // ── Pricing state ──
  const [products, setProducts] = useState([]);
  const [pricingError, setPricingError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [quotedAt, setQuotedAt] = useState(null);
  const [rateSheetDate, setRateSheetDate] = useState(null);
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [eligibleOnly, setEligibleOnly] = useState(true);
  const [sortBy, setSortBy] = useState('price');
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [selectedRate, setSelectedRate] = useState(null);
  const [showAllRates, setShowAllRates] = useState(false);
  const selectedRateDetailRef = useRef(null);
  const [runPricing] = pricingApi.endpoints.runPricing.useMutation();
  const [runLoanPricing] = pricingApi.endpoints.runLoanPricing.useMutation();
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();

  // ── Loan limit state ──
  const [loanLimitWarning, setLoanLimitWarning] = useState(null);
  const [loanLimitInfo, setLoanLimitInfo] = useState(null);

  // ── Computed values ──
  // Purchase: use lower of purchase price or appraised value. Refi: use appraised value.
  const ltvBase = isRefi
    ? (Number(appraisedValue) || 0)
    : Math.min(Number(purchasePrice) || 0, Number(appraisedValue) || 0);
  const ltv = ltvBase > 0 ? ((effectiveLoanAmount / ltvBase) * 100) : 0;
  const cltv = ltvBase > 0 ? (((effectiveLoanAmount + (subordinateFinancing ? (Number(subordinateAmount) || 0) : 0)) / ltvBase) * 100) : 0;
  const downPayment = (Number(purchasePrice) || 0) - effectiveLoanAmount;
  const showPrepay = selectedFamilies.includes('Non-QM');

  // ── Auto-manage product family filters based on inputs ──
  useEffect(() => {
    setSelectedFamilies(prev => {
      const base = new Set(prev);

      // Veteran → add VA
      if (veteran) base.add('VA'); else base.delete('VA');

      // Streamline: FHA streamline vs VA IRRRL (veteran + streamline → VA, not FHA)
      if (docType === 'Streamline') {
        if (veteran) {
          base.add('VA');
          base.delete('FHA');
        } else {
          base.add('FHA');
          base.delete('VA');
        }
      }

      if (docType !== 'FullDoc' && docType !== 'Streamline') {
        base.add('Non-QM');
      }

      if (occupancy === 'NonOwnerOccupied') {
        base.add('Non-QM');
      }

      const next = [...base];
      if (next.length === prev.length && next.every(f => prev.includes(f))) return prev;
      return next;
    });
  }, [veteran, docType, occupancy]);

  // ── Build pricing request directly from form values ──
  /** @param {{ productFamilies?: string[], terms?: number[] }} [overrides] Pass when running pricing before React state (e.g. filters) has flushed */
  const buildRequest = useCallback((overrides = {}) => {
    const v = getValues();
    const families = overrides.productFamilies ?? selectedFamilies;
    const terms = termsForPricingRequest(overrides.terms ?? selectedTerms, families);
    const _isRefi = v.loanPurpose === 'RateTermRefinance' || v.loanPurpose === 'CashoutRefinance';
    const _isCashout = v.loanPurpose === 'CashoutRefinance';
    // Form field loanAmount → pricing engine loan.baseLoanAmount (not added to cashout).
    const baseLoanAmount = Number(v.loanAmount) || 0;
    const _ltvBase = _isRefi
      ? (Number(v.appraisedValue) || 0)
      : Math.min(Number(v.purchasePrice) || 0, Number(v.appraisedValue) || 0);
    const _ltv = _ltvBase > 0 ? ((baseLoanAmount / _ltvBase) * 100) : 0;
    const _cltv = _ltvBase > 0 ? (((baseLoanAmount + (v.subordinateFinancing ? (Number(v.subordinateAmount) || 0) : 0)) / _ltvBase) * 100) : 0;
    const _downPayment = (Number(v.purchasePrice) || 0) - baseLoanAmount;
    const _showPrepay = families.includes('Non-QM');
    const _fipsCode = v.selectedCounty || '';
    const pricingChannel = showChannelSelector ? v.channel : 'Wholesale';
    const isBasePricing = v.brokerCompanyId === BASE_PRICING_BROKER_ID;
    const optsBrokerCompanyId = isBasePricing
      ? undefined
      : (selectedWholesaleBrokerCompany?.id ?? (isBrokerRole ? myBrokerCompany?.id : undefined));

    return {
      borrower: {
        citizenship: v.citizenship,
        debtToIncomeRatio: v.dti,
        isFirstTimeHomeBuyer: v.firstTimeBuyer,
        isSelfEmployed: v.selfEmployed,
        monthsReserves: v.monthsReserves !== '' ? Number(v.monthsReserves) : 0,
        ...(v.docType === 'DSCR' && v.dscrRatio !== '' ? { debtServiceCoverageRatio: Number(v.dscrRatio) } : {}),
        ...(v.docType === 'BankStatements' ? { bankStatement: v.bankStatementType } : {}),
        rentalHistoryMonths: v.occupancy === 'NonOwnerOccupied' ? 12 : 0,
      },
      loan: {
        baseLoanAmount,
        cltv: Math.round(_cltv * 100) / 100,
        compensationPoints: v.compensationType === 'BPC' && v.bpcValue
          ? (v.bpcCalcMethod === 'PercentOfLoan' ? parseFloat(v.bpcValue) || 0 : parseFloat(v.bpcValue) || 0)
          : 0,
        compensationType: v.compensationType,
        docType: v.docType,
        downPaymentAmount: !_isRefi && _downPayment > 0 ? _downPayment : 0,
        escrowType: v.escrowType,
        isSubordinateFinancing: v.subordinateFinancing,
        loanPurpose: v.loanPurpose,
        ltv: Math.round(_ltv * 100) / 100,
        representativeFico: v.creditScore,
        submissionDate: new Date().toISOString(),
        subordinateFinancingAmount: v.subordinateFinancing ? (Number(v.subordinateAmount) || 0) : 0,
        // Cash-out only: informational portion of baseLoanAmount (engine key cashoutAmount).
        ...(_isCashout ? { cashoutAmount: Number(v.cashoutAmount) || 0 } : {}),
        prepayOptions: (_showPrepay && v.prepayPenaltyTerm !== 'None') ? v.prepayPenaltyTerm : 'None',
      },
      options: {
        channel: pricingChannel,
        branchCode: 'West',
        includeRulesResults: true,
        lockTerm: v.lockDuration,
        ...(pricingChannel === 'Wholesale' && selectedWholesaleBrokerCompany?.compId ? { userId: selectedWholesaleBrokerCompany.compId } : {}),
        ...(optsBrokerCompanyId ? { brokerCompanyId: optsBrokerCompanyId } : {}),
        ...(families.length > 0 ? { productFamilies: [...families] } : {}),
        ...(terms.length > 0 ? { terms } : {}),
      },
      subjectProperty: {
        appraisedValue: v.appraisedValue,
        occupancy: v.occupancy,
        propertyType: v.propertyType,
        ...(!_isRefi ? { purchasePrice: v.purchasePrice } : {}),
        state: v.propertyState,
        ...(_fipsCode ? { fipsCode: _fipsCode } : {}),
        ...(v.propertyType === 'MultiUnit' ? { numberOfUnits: v.numberOfUnits } : {}),
      },
    };
  }, [getValues, isBrokerRole, myBrokerCompany?.id, selectedFamilies, selectedTerms, selectedWholesaleBrokerCompany, showChannelSelector]);

  // ── Run Pricing ──
  /** @param {{ productFamilies?: string[], terms?: number[] }} [runOpts] Optional filter overrides (used by quick scenarios before state updates flush) */
  const handleRunPricing = useCallback(async (runOpts = {}) => {
    const values = getValues();
    const pricingChannel = showChannelSelector ? values.channel : 'Wholesale';
    if (pricingChannel === 'Wholesale' && !values.brokerCompanyId) {
      setPricingError('Broker company is required when channel is Wholesale.');
      return;
    }

    const familiesForRun = runOpts.productFamilies ?? selectedFamilies;
    const termsForRun = termsForPricingRequest(runOpts.terms ?? selectedTerms, familiesForRun);

    const numericLoanAmount = Number(values.loanAmount) || 0;
    const numberOfUnitsForLimit = Number(values.numberOfUnits) || 1;
    const fipsForLimit = values.selectedCounty || '';

    setPricingError(null);
    setProducts([]);
    setExpandedProducts(new Set());
    setSelectedRate(null);
    setShowAllRates(false);
    setIsRunning(true);
    setLoanLimitWarning(null);
    setLoanLimitInfo(null);

    try {
      const request = buildRequest({ productFamilies: familiesForRun, terms: termsForRun });

      const activeFamilies = familiesForRun.length > 0 ? familiesForRun : [];
      const limitFamilies = activeFamilies.filter((f) => appliesCountyLoanLimit(f));

      if (limitFamilies.length > 0 && numericLoanAmount > 0) {
        const { countyLimitsCache, countyLimitUnavailable } = await fetchCountyLimitsForPricing(
          fetchCountyLimit,
          fipsForLimit,
          limitFamilies,
        );

        const {
          perFamilyResults,
          anyExceedsStandard,
          allExceedCounty,
        } = evaluateCountyLoanLimits(
          numericLoanAmount,
          numberOfUnitsForLimit,
          limitFamilies,
          countyLimitsCache,
          countyLimitUnavailable,
          fipsForLimit,
        );

        let hasGovernmentExceed = false;
        let hasConventionalExceed = false;
        const limitMessages = [];

        for (const result of perFamilyResults) {
          if (result.message) limitMessages.push(result.message);
          if (result.exceedsStandardLimit) {
            if (['FHA', 'VA', 'USDA'].includes(result.family)) hasGovernmentExceed = true;
            else hasConventionalExceed = true;
          }
        }

        // Always set loanLimitInfo so county limit badges display on product cards
        const worstType = perFamilyResults.some(r => r.warningType === 'error') ? 'error' : 
                          perFamilyResults.some(r => r.warningType === 'warning') ? 'warning' : null;
        if (limitMessages.length > 0) {
          setLoanLimitWarning(limitMessages.join('\n'));
        }
        setLoanLimitInfo({ warningType: worstType, perFamily: perFamilyResults });

        // Block pricing only if ALL selected families exceed their county limit
        if (allExceedCounty && limitFamilies.length > 0 && perFamilyResults.some(r => r.exceedsCountyLimit)) {
          setIsRunning(false);
          return;
        }

        // Auto-inject subfamilies if any family exceeds its standard limit
        if (anyExceedsStandard) {
          const subFamilies = new Set();
          if (hasGovernmentExceed) subFamilies.add('High Balance');
          if (hasConventionalExceed) {
            subFamilies.add('High Balance');
            subFamilies.add('Super Conforming');
          }
          request.options.productSubFamilies = [...subFamilies];
        }
      }

      const response = await runPricingWithEngine({
        featureFlags,
        pricingRequest: request,
        loanId: null,
        runPricing,
        runLoanPricing,
      });
      setProducts(response.products || []);
      setQuotedAt(response.quotedAt || new Date().toISOString());
      setRateSheetDate(response.rateSheetId || null);
      const snap = { ...request, loan: { ...request.loan } };
      delete snap.loan.submissionDate;
      setPricingSnapshot(JSON.stringify(snap));
    } catch (err) {
      console.error('[ScenarioPricer] pricing error:', err);
      setPricingError(getAxiosErrorMessage(err, 'Failed to fetch pricing.'));
    } finally {
      setIsRunning(false);
    }
  }, [buildRequest, fetchCountyLimit, getValues, runPricing, runLoanPricing, featureFlags, selectedFamilies, selectedTerms, showChannelSelector]);

  const applyQuickScenario = useCallback((scenario) => {
    const keepBroker = getValues('brokerCompanyId');
    const keepChannel = getValues('channel');
    reset({
      ...QUICK_PRICER_BASE_DEFAULTS,
      ...scenario.formPatch,
      brokerCompanyId: keepBroker,
      channel: keepChannel,
    });
    const fam = scenario.families;
    const tm = scenario.terms ?? [30];
    setSelectedFamilies(fam);
    setSelectedTerms(tm);
    window.setTimeout(() => {
      handleRunPricing({ productFamilies: fam, terms: tm });
    }, 75);
  }, [getValues, handleRunPricing, reset]);

  // ── Auto re-run pricing only when switching back to LPC (no extra input needed) ──
  const compInitialRender = useRef(true);
  useEffect(() => {
    if (compInitialRender.current) { compInitialRender.current = false; return; }
    if (compensationType === 'LPC' && products.length > 0 && !isRunning) handleRunPricing();
  }, [compensationType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stale pricing detection ──
  const isPricingStale = useMemo(() => {
    if (!pricingSnapshot || products.length === 0) return false;
    try {
      const req = buildRequest();
      delete req.loan?.submissionDate;
      return JSON.stringify(req) !== pricingSnapshot;
    } catch { return false; }
  }, [buildRequest, pricingSnapshot, products]);

  // ── Filter & sort products ──
  const filteredProducts = useMemo(() => {
    let list = eligibleOnly
      ? products.filter(p => p.isEligible)
      : [...products].sort((a, b) => (b.isEligible ? 1 : 0) - (a.isEligible ? 1 : 0));

    if (sortBy === 'price') {
      list = [...list].sort((a, b) => {
        const aPrice = a.parRate?.adjustedPrice ?? -Infinity;
        const bPrice = b.parRate?.adjustedPrice ?? -Infinity;
        return bPrice - aPrice;
      });
    } else if (sortBy === 'payment') {
      const getPayment = (p) => {
        if (!p.parRate) return Infinity;
        return p.paymentType === 'Interest Only'
          ? (effectiveLoanAmount * (p.parRate.rate / 100) / 12)
          : (() => { const r = p.parRate.rate / 100 / 12; const n = p.term * 12; return r === 0 ? effectiveLoanAmount / n : (effectiveLoanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1); })();
      };
      list = [...list].sort((a, b) => getPayment(a) - getPayment(b));
    }
    return list;
  }, [products, eligibleOnly, sortBy, effectiveLoanAmount]);

  return (
    <>
      <PageHeader
        title="Quick Pricer"
        subtitle="Fast pricing — no loan required. All fields map directly to the pricing engine."
        actions={quotedAt && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Quoted {new Date(quotedAt).toLocaleTimeString()}</Typography>
            {rateSheetDate && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Rates Updated: {new Date(rateSheetDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} {new Date(rateSheetDate).toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        )}
      />

      <Grid container spacing={2}>
        {/* ─── INPUT PANEL ─── */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ position: 'sticky', top: '1rem' }}>
            <CardHeader title="Pricing Inputs" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} sx={{ pb: 0 }} />
            <CardContent sx={{ maxHeight: 'calc(100vh - 200px)', px: 1, overflowY: 'auto' }}>
            <FormProvider methods={methods}>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Quick scenarios</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', lineHeight: 1.35 }}>
                Fills inputs and runs pricing. Adjust afterward if needed.
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {QUICK_SCENARIOS.map((sc) => (
                  <Grid key={sc.id} size={{ xs: 6 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => applyQuickScenario(sc)}
                      disabled={isRunning || (showStaffBrokerPicker && isWholesale && !brokerCompanyId)}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'center',
                        py: 0.4,
                        px: 0.5,
                        minHeight: 32,
                        fontSize: '0.7rem',
                        lineHeight: 1.2,
                        whiteSpace: 'normal',
                        textAlign: 'center',
                      }}
                    >
                      {sc.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {showChannelSelector && (
                <>
                  <Box sx={{ mb: 1.5 }}>
                    <SelectField
                      name="channel"
                      label="Channel"
                      size="small"
                      options={CHANNEL_OPTIONS}
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              {showStaffBrokerPicker && (
                <>
                  <Box sx={{ mb: 1.5 }}>
                    <SelectField
                      name="brokerCompanyId"
                      label="Broker Company"
                      size="small"
                      options={brokerCompanyOptions}
                      required
                      disabled={brokerCompanyOptions.length === 0}
                    />
                    {brokerCompanyOptions.length === 0 && (
                      <Typography variant="caption" color="warning.main">
                        No broker companies available for your account.
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* ── Loan ── */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <AttachMoneyIcon color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" color="primary" fontWeight={700}>Loan</Typography>
              </Stack>

              <Box sx={{ mb: 1.5 }}>
                <SelectField
                  name="loanPurpose"
                  label="Loan Purpose"
                  size="small"
                  options={LOAN_PURPOSE_OPTIONS}
                  onChange={() => setValue('cashoutAmount', '')}
                />
              </Box>

              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={12}>
                  <CurrencyField name="loanAmount" label="Loan Amount" placeholder={isRefi ? 'e.g. 350,000' : undefined} />
                </Grid>
                {isCashout && (
                  <Grid size={12}>
                    <CurrencyField
                      name="cashoutAmount"
                      label="Cashout Amount"
                      placeholder="e.g. 50,000"
                      helperText="How much of the loan amount is cash out to the borrower?"
                    />
                  </Grid>
                )}
                {!isRefi && (
                  <Grid size={{ xs: 6 }}>
                    <CurrencyField name="purchasePrice" label="Purchase Price" />
                  </Grid>
                )}
                <Grid size={{ xs: isRefi ? 12 : 6 }}>
                  <CurrencyField name="appraisedValue" label="Appraised Value" />
                </Grid>
              </Grid>

              <Grid container spacing={1} sx={{ my: 1.5 }}>
                <Grid size={{ xs: isRefi ? 6 : 4 }}>
                  <MuiTextField label="LTV" size="small" fullWidth value={`${ltv.toFixed(2)}%`} disabled />
                </Grid>
                <Grid size={{ xs: isRefi ? 6 : 4 }}>
                  <MuiTextField label="CLTV" size="small" fullWidth value={`${cltv.toFixed(2)}%`} disabled />
                </Grid>
                {!isRefi && (
                  <Grid size={{ xs: 4 }}>
                    <MuiTextField label="Down Pmt" size="small" fullWidth value={fmtDollar(downPayment > 0 ? downPayment : 0)} disabled />
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="docType" label="Doc Type" size="small" options={DOC_TYPE_OPTIONS} onChange={(e) => { if (e.target.value !== 'DSCR') setValue('dscrRatio', ''); if (e.target.value !== 'BankStatements') setValue('bankStatementType', 'PersonalTwelveMonths'); }} />
              </Box>

              {docType === 'BankStatements' && (
                <Box sx={{ mb: 1.5 }}>
                  <SelectField name="bankStatementType" label="Bank Statement Type" size="small" options={BANK_STATEMENT_TYPE_OPTIONS} />
                </Box>
              )}

              {docType === 'DSCR' && (
                <NumberField name="dscrRatio" label="DSCR Ratio" size="small" fullWidth step={0.001} min={0} placeholder="e.g. 1.250" sx={{ mb: 1.5 }} />
              )}

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="escrowType" label="Escrow" size="small" options={ESCROW_OPTIONS} />
              </Box>

              {showPrepay && (
                <Box sx={{ mb: 1.5 }}>
                  <SelectField name="prepayPenaltyTerm" label="Prepay Penalty" size="small" options={PREPAY_OPTIONS} />
                </Box>
              )}

              <Box sx={{ ml: 1.5 }}>
                <SwitchField name="subordinateFinancing" label="Subordinate Financing" sx={{ mb: subordinateFinancing ? 0.5 : 1 }} />
              </Box>
              {subordinateFinancing && (
                <Box sx={{ mb: 1.5}}>
                  <CurrencyField name="subordinateAmount" />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* ── Property ── */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <HomeIcon color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" color="primary" fontWeight={700}>Property</Typography>
              </Stack>

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="propertyState" label="State" size="small" options={STATE_OPTIONS} onChange={() => setValue('selectedCounty', '')} />
              </Box>

              <MuiTextField select label="County" size="small" fullWidth value={selectedCounty} onChange={e => setValue('selectedCounty', e.target.value)} disabled={isFetchingCounties} slotProps={{ select: { MenuProps: { disableScrollLock: true } } }} sx={{ mb: 1.5 }}>
                {counties.map(c => <MenuItem key={c.fipsCode} value={c.fipsCode}>{toProperCase(c.countyName)}</MenuItem>)}
              </MuiTextField>
              {isFetchingCounties && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Loading counties…</Typography>
                </Stack>
              )}

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="propertyType" label="Property Type" size="small" options={PROPERTY_TYPE_OPTIONS} onChange={(e) => setValue('numberOfUnits', e.target.value === 'MultiUnit' ? 2 : 1)} />
              </Box>

              {propertyType === 'MultiUnit' && (
                <Box sx={{ mb: 1.5 }}>
                  <SelectField name="numberOfUnits" label="Units" size="small" options={UNIT_OPTIONS} />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* ── Borrower ── */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <PersonIcon color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" color="primary" fontWeight={700}>Borrower</Typography>
              </Stack>

              <NumberField name="creditScore" label="Credit Score" size="small" fullWidth min={300} max={850} sx={{ mb: 1.5 }} />

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="citizenship" label="Citizenship" size="small" options={CITIZENSHIP_OPTIONS} />
              </Box>

              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 6 }}>
                  <NumberField name="dti" label="DTI Ratio (%)" size="small" fullWidth step={0.5} min={0} max={65} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SelectField name="occupancy" label="Occupancy" size="small" options={OCCUPANCY_OPTIONS} />
                </Grid>
              </Grid>

              <NumberField name="monthsReserves" label="Months of Reserves" size="small" fullWidth min={0} max={60} sx={{ mb: 1.5 }} />

              <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb: 1 }}>
                <SwitchField name="firstTimeBuyer" label="First-Time Buyer" />
                <SwitchField name="selfEmployed" label="Self-Employed" />
                <SwitchField name="veteran" label="Veteran" />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* ── Filters ── */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" color="primary" fontWeight={700}>Filters</Typography>
              </Stack>

              <Box sx={{ mb: 1.5 }}>
                <SelectField name="lockDuration" label="Lock Term" size="small" options={LOCK_DURATION_OPTIONS} />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Product Families</Typography>
              <ToggleButtonGroup size="small" value={selectedFamilies} onChange={(_, val) => setSelectedFamilies(val)} sx={{ flexWrap: 'wrap', mb: 1.5 }}>
                {PRODUCT_FAMILIES.map(f => (
                  <ToggleButton key={f.id} value={f.id} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}>{f.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Loan Terms</Typography>
              <ToggleButtonGroup size="small" value={selectedTerms} onChange={(_, val) => setSelectedTerms(val)} sx={{ flexWrap: 'wrap', mb: 2 }}>
                {loanTermOptions.map((t) => (
                  <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}>{t}yr</ToggleButton>
                ))}
              </ToggleButtonGroup>

              {loanLimitWarning && loanLimitInfo?.perFamily?.filter(r => r.warningType === 'error').length > 0 && (
                <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                  {loanLimitInfo.perFamily.filter(r => r.warningType === 'error').map((r, i) => (
                    <Typography key={i} variant="caption">{r.message}</Typography>
                  ))}
                </Alert>
              )}
              {loanLimitWarning && loanLimitInfo?.perFamily?.filter(r => r.warningType === 'warning').length > 0 && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  {loanLimitInfo.perFamily.filter(r => r.warningType === 'warning').map((r, i) => (
                    <Typography key={i} variant="caption">{r.message}</Typography>
                  ))}
                </Alert>
              )}

              <Button variant="contained" fullWidth onClick={() => handleRunPricing()} disabled={isRunning || (showStaffBrokerPicker && isWholesale && !brokerCompanyId)} startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}>
                {isRunning ? 'Running…' : 'Run Pricing'}
              </Button>
            </FormProvider>
            </CardContent>
          </Card>
        </Grid>

        {/* ─── RESULTS PANEL ─── */}
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Loan limit errors */}
          {loanLimitInfo?.perFamily?.filter(r => r.warningType === 'error').length > 0 && (
            <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>Loan Amount Exceeds County Limit</Typography>
              {loanLimitInfo.perFamily.filter(r => r.warningType === 'error').map((r, i) => (
                <Typography key={i} variant="body2">{r.message}</Typography>
              ))}
            </Alert>
          )}
          {/* Loan limit warnings */}
          {loanLimitInfo?.perFamily?.filter(r => r.warningType === 'warning').length > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>High-Cost Area</Typography>
              {loanLimitInfo.perFamily.filter(r => r.warningType === 'warning').map((r, i) => (
                <Typography key={i} variant="body2">{r.message}</Typography>
              ))}
            </Alert>
          )}

          {pricingError && <Alert severity="error" sx={{ mb: 2 }}>{pricingError}</Alert>}

          {/* Stale pricing warning */}
          {isPricingStale && (
            <Alert
              severity="warning"
              sx={{ mb: 2, borderLeft: 4, borderColor: 'warning.main' }}
              action={
                <Button color="warning" size="small" onClick={handleRunPricing} disabled={isRunning} startIcon={<RefreshIcon />}>
                  Re-run
                </Button>
              }
            >
              <strong>Pricing inputs have changed.</strong> The prices shown may no longer be accurate.
            </Alert>
          )}

          {/* ── Compensation bar ── */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AttachMoneyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">Compensation</Typography>
              </Stack>
              <MuiTextField select size="small" value={compensationType} onChange={e => { setValue('compensationType', e.target.value); if (e.target.value === 'LPC') setValue('bpcValue', ''); }} slotProps={{ select: { MenuProps: { disableScrollLock: true } } }} sx={{ width: 180 }}>
                <MenuItem value="LPC">LPC (Lender Paid)</MenuItem>
                <MenuItem value="BPC">BPC (Borrower Paid)</MenuItem>
              </MuiTextField>
              {compensationType === 'LPC' && (
                <Box>
                  {isLoadingComp ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">Loading…</Typography>
                    </Stack>
                  ) : activeCompensation ? (
                    <Typography variant="caption">
                      <strong>{activeCompensation.percentage}%</strong>
                      {(activeCompensation.minDollarAmount != null || activeCompensation.maxDollarAmount != null) && (
                        <Typography component="span" variant="caption" color="text.secondary"> (${(activeCompensation.minDollarAmount || 0).toLocaleString()}–${(activeCompensation.maxDollarAmount || 0).toLocaleString()})</Typography>
                      )}
                      {lpcCompDollars != null && lpcCompDollars > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>Est: <strong>{fmtDollar(lpcCompDollars)}</strong></Typography>
                      )}
                    </Typography>
                  ) : null}
                </Box>
              )}
              {compensationType === 'BPC' && (
                <>
                  <MuiTextField select size="small" value={bpcCalcMethod} onChange={e => { setValue('bpcCalcMethod', e.target.value); setValue('bpcValue', ''); }} slotProps={{ select: { MenuProps: { disableScrollLock: true } } }} sx={{ width: 120 }}>
                    <MenuItem value="PercentOfLoan">Percent</MenuItem>
                    <MenuItem value="FixedAmount">Fixed Amount</MenuItem>
                  </MuiTextField>
                  {bpcCalcMethod === 'FixedAmount' ? (
                    <CurrencyInput size="small" value={bpcValue} onChange={val => setValue('bpcValue', val)} placeholder="e.g. 5,000" sx={{ width: 130 }} />
                  ) : (
                    <MuiTextField size="small" type="number" value={bpcValue} onChange={e => setValue('bpcValue', e.target.value)} inputProps={{ step: 0.125, min: 0 }} placeholder="e.g. 1.5" InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} sx={{ width: 130 }} />
                  )}
                </>
              )}
            </Stack>
          </Paper>

          {compensationType === 'LPC' && isBasePricingSelection && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Running using Base Pricing - Pricing will not have any LPC Compensation
              </Typography>
            </Alert>
          )}

          {compensationType === 'LPC' && selectedWholesaleBrokerCompany?.id && !isLoadingComp && !activeCompensation && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>No active compensation schedule</Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                This company does not have an active compensation plan. LPC pricing adjustments are not included in this pricing scenario.
                {compensationManagementHref && isSelectedBrokerApproved && (
                  <>
                    {' '}
                    <Link
                      component={RouterLink}
                      to={compensationManagementHref}
                      underline="hover"
                      sx={{ fontWeight: 600, color: 'warning.dark' }}
                    >
                      Go to Compensation
                    </Link>
                  </>
                )}
                {compensationManagementHref && !isSelectedBrokerApproved && (
                  <> Compensation will be set up once your account is fully activated.</>
                )}
              </Typography>
            </Alert>
          )}

          {/* Empty state */}
          {products.length === 0 && !isRunning && !pricingError && (
            <Card>
              <EmptyState
                icon={<CalculateIcon />}
                title="Ready to Price"
                description={'Configure your scenario and click "Run Pricing" to fetch products and rates from the pricing engine.'}
              />
            </Card>
          )}

          {/* Loading */}
          {isRunning && (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Running Pricing Engine…</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Querying products and rate sheets</Typography>
              <LoadingSkeleton variant="cards" cards={4} />
            </Card>
          )}

          {/* Results */}
          {products.length > 0 && !isRunning && (() => {
            const eligibleCount = products.filter(p => p.isEligible).length;
            const ineligibleCount = products.length - eligibleCount;

            return (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      {filteredProducts.length} Product{filteredProducts.length !== 1 ? 's' : ''}
                    </Typography>
                    {!eligibleOnly && ineligibleCount > 0 && (
                      <>
                        <StatusBadge variant="success" sx={{ mr: 0.5 }}>{eligibleCount} eligible</StatusBadge>
                        <StatusBadge variant="danger">{ineligibleCount} ineligible</StatusBadge>
                      </>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">Sort by</Typography>
                      <MuiTextField select size="small" value={sortBy} onChange={e => setSortBy(e.target.value)} slotProps={{ select: { MenuProps: { disableScrollLock: true } } }} sx={{ width: 140 }}>
                        <MenuItem value="price">Best Price</MenuItem>
                        <MenuItem value="payment">Lowest Payment</MenuItem>
                      </MuiTextField>
                    </Stack>
                    <FormControlLabel
                      control={<Switch size="small" checked={eligibleOnly} onChange={e => setEligibleOnly(e.target.checked)} />}
                      label={<Typography variant="caption">Eligible only</Typography>}
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={2}>
                  {filteredProducts.map((p) => {
                    const familyLimit = loanLimitInfo?.perFamily?.find(r => r.family === p.family)
                      || ((['VA', 'USDA'].includes(p.family)) ? loanLimitInfo?.perFamily?.find(r => r.family === 'FHA') : null);

                    return (
                      <Grid size={{ xs: 12, md: 6 }} key={p.productShortName}>
                        <ProductCard
                          product={p}
                          loanAmount={effectiveLoanAmount}
                          selectedRate={selectedRate}
                          onSelectRate={(rate) => {
                            setSelectedRate(rate);
                            setTimeout(() => selectedRateDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                          }}
                          isExpanded={expandedProducts.has(p.productShortName)}
                          onToggleExpand={() => setExpandedProducts(prev => { const next = new Set(prev); if (next.has(p.productShortName)) next.delete(p.productShortName); else next.add(p.productShortName); return next; })}
                          showAllRates={showAllRates}
                          onToggleShowAll={() => setShowAllRates(prev => !prev)}
                          compensationType={compensationType}
                          familyLimit={familyLimit}
                        />
                      </Grid>
                    );
                  })}
                </Grid>

                {/* ── Selected rate detail ── */}
                {selectedRate && filteredProducts.find(p => p.productShortName === selectedRate.productShortName) && (
                  <SelectedRateDetail
                    ref={selectedRateDetailRef}
                    selectedRate={selectedRate}
                    loanAmount={effectiveLoanAmount}
                    compensationType={compensationType}
                    activeCompensation={activeCompensation}
                    bpcCalcMethod={bpcCalcMethod}
                    bpcValue={bpcValue}
                    lockDuration={lockDuration}
                    onClose={() => { setSelectedRate(null); setExpandedProducts(new Set()); setShowAllRates(false); }}
                  />
                )}
              </>
            );
          })()}
        </Grid>
      </Grid>

    </>
  );
};

export default ScenarioPricerPage;
